import React, { useState } from "react";
import { Mail, Send, Briefcase, User, UserCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { useContent } from "../context/ContentContext";
import { submitInquiry } from "../lib/api";

const FORMSPREE_URL = process.env.REACT_APP_FORMSPREE_URL;

const Contact = () => {
  const { player } = useContent();
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    inquiryType: "recruiting",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "Missing details",
        description: "Please fill in your name, email, and a short message.",
      });
      return;
    }
    setSubmitting(true);
    try {
      // Try Formspree first (works on Vercel/static hosting)
      if (FORMSPREE_URL) {
        const res = await fetch(FORMSPREE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            organization: form.organization,
            inquiryType: form.inquiryType,
            message: form.message,
            _subject: `[Scout Me] ${form.inquiryType.toUpperCase()} \u2014 ${form.name}`,
          }),
        });
        if (!res.ok) throw new Error("Formspree submit failed");
      } else {
        await submitInquiry(form);
      }
      // Track in Google Analytics if available
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "inquiry_submitted", {
          inquiry_type: form.inquiryType,
        });
      }
      toast({
        title: "Inquiry sent",
        description: "Thanks! DaMarkas's team will get back to you shortly.",
      });
      setForm({
        name: "",
        email: "",
        organization: "",
        inquiryType: "recruiting",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email directly.";
      toast({
        title: "Couldn't send inquiry",
        description: typeof detail === "string" ? detail : "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-neutral-950 border-t border-neutral-900">
      <div className="absolute inset-0 field-lines opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="accent-bar" />
            <span className="text-xs uppercase tracking-[0.35em] text-neutral-400 font-heading">
              07 — Contact
            </span>
          </div>
          <h2 className="font-display text-5xl md:text-6xl text-white leading-none">
            LET'S <br />
            <span className="text-red-600">TALK BALL</span>
          </h2>
          <p className="mt-6 text-neutral-300 max-w-md">
            For recruiting inquiries, camp invites, brand deals, or interviews —
            reach out below or contact directly via email.
          </p>

          <div className="mt-8 space-y-4">
            <a
              href={`mailto:${player.recruitingEmail}`}
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 rounded-sm bg-red-600/10 border border-red-600/30 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-red-500" />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-heading">
                  Recruiting / Brand Deals
                </div>
                <div className="font-heading text-white group-hover:text-red-500 transition-colors">
                  {player.recruitingEmail}
                </div>
              </div>
            </a>
            <a
              href={`mailto:${player.contactEmail}`}
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 rounded-sm bg-red-600/10 border border-red-600/30 flex items-center justify-center">
                <Mail className="w-5 h-5 text-red-500" />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-heading">
                  Personal Contact
                </div>
                <div className="font-heading text-white group-hover:text-red-500 transition-colors">
                  {player.contactEmail}
                </div>
              </div>
            </a>
            {player.coachEmail && (
              <a
                href={`mailto:${player.coachEmail}`}
                className="flex items-center gap-4 group pt-4 border-t border-neutral-800"
              >
                <span className="w-11 h-11 rounded-sm bg-red-600/10 border border-red-600/30 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-red-500" />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-heading">
                    Contact My Coach
                  </div>
                  <div className="font-heading text-white group-hover:text-red-500 transition-colors">
                    {player.coachEmail}
                  </div>
                  <div className="text-xs text-neutral-400 mt-0.5">
                    {player.coachName} · {player.coachRole}
                  </div>
                </div>
              </a>
            )}
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="lg:col-span-7 border border-neutral-800 bg-neutral-900/60 rounded-sm p-6 md:p-8"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs uppercase tracking-[0.25em] font-heading text-neutral-300">
                Your Name
              </Label>
              <Input
                id="name"
                value={form.name}
                onChange={onChange("name")}
                placeholder="Coach / Recruiter Name"
                className="bg-neutral-950 border-neutral-800 text-white h-11 rounded-sm focus-visible:ring-red-600"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.25em] font-heading text-neutral-300">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                placeholder="you@school.edu"
                className="bg-neutral-950 border-neutral-800 text-white h-11 rounded-sm focus-visible:ring-red-600"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org" className="text-xs uppercase tracking-[0.25em] font-heading text-neutral-300">
                Organization / School
              </Label>
              <Input
                id="org"
                value={form.organization}
                onChange={onChange("organization")}
                placeholder="Program / Brand"
                className="bg-neutral-950 border-neutral-800 text-white h-11 rounded-sm focus-visible:ring-red-600"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.25em] font-heading text-neutral-300">
                Inquiry Type
              </Label>
              <Select value={form.inquiryType} onValueChange={onChange("inquiryType")}>
                <SelectTrigger className="bg-neutral-950 border-neutral-800 text-white h-11 rounded-sm focus:ring-red-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-neutral-900 border-neutral-800 text-white">
                  <SelectItem value="recruiting">Recruiting</SelectItem>
                  <SelectItem value="camp">Camp Invite</SelectItem>
                  <SelectItem value="brand">Brand Deal / NIL</SelectItem>
                  <SelectItem value="media">Media / Interview</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5 space-y-2">
            <Label htmlFor="message" className="text-xs uppercase tracking-[0.25em] font-heading text-neutral-300">
              Message
            </Label>
            <Textarea
              id="message"
              value={form.message}
              onChange={onChange("message")}
              rows={5}
              placeholder="Share your interest, camp dates, or opportunity..."
              className="bg-neutral-950 border-neutral-800 text-white rounded-sm focus-visible:ring-red-600"
            />
          </div>

          <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-neutral-400 text-xs">
              <User className="w-4 h-4" />
              Responses typically within 48 hours
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="btn-primary bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-widest rounded-sm h-11 px-7"
            >
              {submitting ? "Sending..." : (
                <>
                  Send Inquiry <Send className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Contact;
